// Fetching a sim's files from `/sims/…`. Each file is fetched once: JSON and bytes are cached per
// URL (in-flight requests are shared), so readers can ask lazily and as often as they like.

import { decode, descriptorBytes, dtypeOfPath, elementCount, parseDType, type ArrayDescriptor, type DecodeOptions, type Typed } from './binary.ts';

export interface SimSource {
  /** URL prefix that serves `sims/`, ending in a slash: '/sims/'. */
  base: string;
  fetch: typeof fetch;
  /** Whether the server lists its directories (`<dir>/?ls`; sims/ does, packs/ doesn't). Default true. */
  listing?: boolean;
}

export const defaultSource = (): SimSource => ({ base: '/sims/', fetch: (...a) => fetch(...a) });

export class NotFound extends Error {}

/** Join and normalize a relative path ('a/b', '../c' → 'a/c'); refuses to climb above the root. */
export function joinPath(dir: string, rel: string): string {
  const out: string[] = [];
  for (const part of `${dir}/${rel}`.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') {
      if (!out.length) throw new Error(`${rel} climbs out of sims/`);
      out.pop();
    } else out.push(part);
  }
  return out.join('/');
}

const cache = new Map<string, Promise<unknown>>();
const listings = new Map<string, { at: number; names: Promise<Set<string> | null> }>();

/** Forget cached files and listings (tests; a dev reload of a changed run). */
export function clearFileCache() {
  cache.clear();
  listings.clear();
}

/** How long a directory listing is trusted: long enough to cover a scene's lookups, short enough that a
 *  run synced in mid-session turns up without a reload. */
const LISTING_MS = 5000;

/** A sims/ directory's entries from the server's listing (`<dir>/?ls`, every root; a directory's name
 *  ends in '/'). Null where the server has no listing, so a caller asks for the file as before. */
export function listing(src: SimSource, dir: string): Promise<Set<string> | null> {
  if (src.listing === false) return Promise.resolve(null);
  const url = `${src.base}${dir ? `${dir.split('/').map(encodeURIComponent).join('/')}/` : ''}?ls`;
  const hit = listings.get(url);
  if (hit && Date.now() - hit.at < LISTING_MS) return hit.names;
  const names = (async () => {
    try {
      const res = await src.fetch(url);
      if (!res.ok) return null;
      const list = (await res.json()) as unknown;
      return Array.isArray(list) && list.every((n) => typeof n === 'string') ? new Set(list as string[]) : null;
    } catch {
      return null;
    }
  })();
  listings.set(url, { at: Date.now(), names });
  return names;
}

/** Whether a file or directory under sims/ is there, by the listings from the top down: false only
 *  when a listing says it isn't, so a run that isn't on this machine never costs a failing request. */
export async function present(src: SimSource, rel: string): Promise<boolean> {
  const parts = rel.split('/').filter(Boolean);
  for (let i = 0; i < parts.length; i++) {
    const names = await listing(src, parts.slice(0, i).join('/'));
    if (!names) return true;
    if (!names.has(`${parts[i]}/`) && !(i === parts.length - 1 && names.has(parts[i]!))) return false;
  }
  return true;
}

function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  let p = cache.get(key) as Promise<T> | undefined;
  if (!p) {
    p = load();
    cache.set(key, p);
    // A failed fetch isn't cached, so a file that appears later (a run still being pulled) can load.
    p.catch(() => cache.delete(key));
  }
  return p;
}

/** One sim directory (or any directory under sims/) and its files. */
export class SimFiles {
  constructor(
    readonly src: SimSource,
    /** Directory under sims/, e.g. 'hemo/ixi165-1d-baseline'. */
    readonly dir: string,
  ) {}

  path(rel: string): string {
    return joinPath(this.dir, rel);
  }

  url(rel: string): string {
    return this.src.base + this.path(rel).split('/').map(encodeURIComponent).join('/');
  }

  /** The directory a relative path's file sits in, as its own SimFiles (descriptor paths resolve from there). */
  at(rel: string): SimFiles {
    const p = this.path(rel);
    return new SimFiles(this.src, p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
  }

  private async get(rel: string): Promise<Response> {
    const url = this.url(rel);
    const res = await this.src.fetch(url);
    if (res.status === 404) throw new NotFound(`${url}: not found`);
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
    return res;
  }

  json<T = unknown>(rel: string): Promise<T> {
    return once(`json:${this.url(rel)}`, async () => (await (await this.get(rel)).json()) as T);
  }

  text(rel: string): Promise<string> {
    return once(`text:${this.url(rel)}`, async () => (await this.get(rel)).text());
  }

  bytes(rel: string): Promise<ArrayBuffer> {
    return once(`bytes:${this.url(rel)}`, async () => (await this.get(rel)).arrayBuffer());
  }

  /** A JSON file, or null when it doesn't exist (an optional index or header). The listings say
   *  first, so a missing file isn't asked for. */
  async maybeJson<T = unknown>(rel: string): Promise<T | null> {
    if (!(await present(this.src, this.path(rel)))) return null;
    try {
      return await this.json<T>(rel);
    } catch (err) {
      if (err instanceof NotFound) return null;
      throw err;
    }
  }

  /** A described array: size checked against shape (and byte_length), decoded to its dtype. */
  async array(d: ArrayDescriptor, opts: Omit<DecodeOptions, 'count'> = {}): Promise<Typed> {
    const bytes = descriptorBytes(d);
    const buf = await this.bytes(d.path);
    if (buf.byteLength !== bytes) throw new Error(`${this.path(d.path)}: ${buf.byteLength} bytes, the descriptor says ${bytes}`);
    return decode(buf, parseDType(d.dtype), { ...opts, count: elementCount(d.shape) });
  }

  /** An undescribed array whose extension names its dtype (cells-sim-v0), optionally with an expected count. */
  async raw(rel: string, opts: DecodeOptions = {}): Promise<Typed> {
    const dtype = dtypeOfPath(rel);
    if (!dtype) throw new Error(`${rel}: no dtype extension`);
    return decode(await this.bytes(rel), dtype, opts);
  }
}
